import { useRoute } from '#imports'

import type {
  BuybackOffer,
  Currency,
  InsuranceOffer,
} from '@backmarket/http-api'
import { getProductInfos } from '@backmarket/nuxt-layer-recommendation/helpers.ts'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { formatPrice } from '@backmarket/nuxt-module-tracking/formatPrice'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { isEmpty } from '@backmarket/utils/object/isEmpty'

import type { CartItem } from '@backmarket/nuxt-layer-checkout/api/cart/types'
import { DEFAULT_INSURANCE_ID } from '@backmarket/nuxt-layer-checkout/constants'

function getPaymentType(insurance?: InsuranceOffer) {
  if (!insurance || insurance.id.toString() === DEFAULT_INSURANCE_ID) return ''

  return insurance.isMonthly ? 'monthly' : 'upfront'
}

export const getTrackingProduct = (item: CartItem, currency: Currency) => {
  const insurance = item.insuranceOffers?.find(
    (offer) => offer.id.toString() !== DEFAULT_INSURANCE_ID && offer.selected,
  )

  const deliveryOption = item.options.find(
    (option) => option.type === 'delivery',
  )
  const deliveryOptionChoices = deliveryOption?.choices || []

  return {
    available: item.available === 'yes',
    brand: item.brand ?? '',
    category: item.category ?? '',
    color: item.color ?? '',
    currency,
    id: item.productId ?? '',
    insurancePrice: insurance?.price.amount ?? '',
    insuranceTitle: insurance?.title ?? '',
    insurancePaymentType: getPaymentType(insurance),
    list: getProductInfos(item.akeneoId).source,
    listingId: item.listingId ?? '',
    merchantId: item.merchantPublicId ?? item.merchantId ?? '',
    model: item.model ?? '',
    name: item.title ?? '',
    specialOfferType: item.specialOfferType,
    price: item.price ?? '',
    quantity: item.quantity ?? '',
    quantityMax: item.quantityMax ?? '',
    collectionPointAvailable: item.options.some((option) =>
      option.choices.some((choice) => choice.isCollectionPoint),
    ),
    shipper: [
      item.shippingSelected.shipperId ?? '',
      item.shippingSelected.shippingFeeDelay ?? '0',
      formatPrice({
        amount: item.shippingSelected.shippingFeePrice,
        currency,
      }),
    ].join(' - '),
    shippingDelay: item.shippingSelected.shippingFeeDelay ?? '',
    shippingPrice: item.shippingSelected.shippingFeePrice ?? '',
    shippingOptions: deliveryOptionChoices.map((choice) =>
      [
        choice.shipperId ?? '',
        choice.shippingDelay ?? '0',
        formatPrice({
          amount: choice.price,
          currency,
        }),
      ].join(' - '),
    ),
    uuid: item.akeneoId ?? '',
    variant: item.gradeId ?? '',
    warrantyDuration: item.warranties[0]?.delay ?? '',

    dealType: 'normal',
    isInsuranceEligible: !!item.insuranceOffers?.length || false,
    insuranceEligibleOffers:
      item.insuranceOffers &&
      item.insuranceOffers
        .filter((offer) => offer.id.toString() !== DEFAULT_INSURANCE_ID)
        .map((offer) => (offer.isMonthly ? 'monthly' : 'upfront'))
        .join('-'),
    mobilePlanOfferSelected: item.mobilePlan?.selectedOffer.name ?? '',
    mobilePlanOfferSelectedType: item.mobilePlan?.selectedOffer.type ?? '',
    isFlashSales: (item.promoCodes?.length ?? 0) > 0,
    campaignId: item.adjustmentFeeCampaignId ?? '',
    populationId: item.adjustmentFeePopulationId ?? '',
  }
}

export function useCheckoutTracking() {
  const route = useRoute()
  const { trackFunnel: track } = useTracking()
  const { defaultCurrency } = useMarketplace().market

  const trackFunnel = (
    items: Array<CartItem>,
    swapOffer: BuybackOffer | null,
  ) => {
    track({
      products: items.map((item) => getTrackingProduct(item, defaultCurrency)),
      swap: !isEmpty(swapOffer),
      swapCategory: !isEmpty(swapOffer)
        ? (swapOffer?.diagnosticPayload?.category as string)
        : null,
      swapEstimation: !isEmpty(swapOffer) ? swapOffer.price : null,
      step: route.meta?.gtmStepId ?? null,
      pageType: (route.name as string) ?? '',
    })
  }

  return { trackFunnel }
}
